import { frameId } from "../assets/manifest.ts";
import type { WorldDocument } from "../document/types.ts";
import { FOOTPRINT_DEFAULT_CELL } from "./footprint-cell.ts";
import { footprintFromCells, type FootprintOverride } from "./footprint-override.ts";
import type { PropFootprint } from "./footprint.ts";
import { addPropFrames } from "./placed-frames.ts";

/**
 * The report of the frames whose library entry still holds 8 px cells (ADR-113 decision 12): which a builder may
 * refit to the art, the most placed in the open world first, each with how far its cells lie from the art as the
 * ADR measures it. Pure: the caller reads the worlds, the library and the pack's art.
 */

/**
 * How a painted footprint lies against the art's own, in pixels laid as the stamp lays them (the frame's bottom
 * edge on a cell edge). `basePixels` is the art's solid band, `blockedWithoutArt` the painted pixels outside it and
 * `artLeftWalkable` the band's pixels outside the painted ones; a share of the band's area is either over `basePixels`.
 */
export interface FootprintMisfit {
  readonly basePixels: number;
  readonly blockedWithoutArt: number;
  readonly artLeftWalkable: number;
}

/**
 * One frame of the report.
 */
export interface RefitRow {
  readonly frameId: string;
  readonly placed: number;
  readonly cells: number;
  readonly misfit: FootprintMisfit | null;
}

/**
 * What the report is made from.
 */
export interface RefitInput {
  readonly overrides: Readonly<Record<string, FootprintOverride>>;
  readonly derived: ReadonlyMap<string, PropFootprint | null>;
  readonly size: (frameId: string) => { readonly w: number; readonly h: number } | undefined;
  readonly placed: ReadonlyMap<string, number>;
}

/**
 * The pixels a footprint marks, as a mask over the frame's box by row from the top.
 *
 * @param footprint The footprint.
 * @param w Frame width.
 * @param h Frame height.
 * @returns One byte a pixel, 1 where marked.
 */
function maskOf(footprint: PropFootprint, w: number, h: number): Uint8Array {
  const mask = new Uint8Array(w * h);
  for (const [rows, start, end] of footprint.runs) {
    const y = footprint.base - 1 - rows;
    if (y < 0 || y >= h) {
      continue;
    }
    for (let x = Math.max(0, start); x <= Math.min(w - 1, end); x += 1) {
      mask[y * w + x] = 1;
    }
  }
  return mask;
}

/**
 * Measures a painted footprint against the art's band.
 *
 * @param base The footprint derived from the art (the band's solid pixels).
 * @param painted The painted footprint, of the same frame size.
 * @returns The pixel counts of the misfit.
 */
export function footprintMisfit(base: PropFootprint, painted: PropFootprint): FootprintMisfit {
  const w = Math.max(base.w, painted.w);
  const h = Math.max(base.h, painted.h);
  const art = maskOf(base, w, h);
  const cells = maskOf(painted, w, h);
  let basePixels = 0;
  let blockedWithoutArt = 0;
  let artLeftWalkable = 0;
  for (let index = 0; index < art.length; index += 1) {
    const inArt = art[index] === 1;
    const inCells = cells[index] === 1;
    basePixels += inArt ? 1 : 0;
    blockedWithoutArt += inCells && !inArt ? 1 : 0;
    artLeftWalkable += inArt && !inCells ? 1 : 0;
  }
  return { basePixels, blockedWithoutArt, artLeftWalkable };
}

/**
 * Counts the props placed of each frame, over worlds: a prop counts once for its own frame and once for each state or
 * animation frame its behaviour names.
 *
 * @param worlds The worlds.
 * @returns The count by frame id.
 */
export function placementCounts(worlds: readonly WorldDocument[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const world of worlds) {
    for (const map of world.maps) {
      for (const layer of map.layers) {
        if (layer.kind !== "prop") {
          continue;
        }
        for (const prop of layer.props) {
          const frames = new Set<string>();
          addPropFrames(prop.frame, prop.behaviour, {}, frames);
          frames.add(frameId(prop.frame.tileset, null, prop.frame.tile));
          for (const frame of frames) {
            counts.set(frame, (counts.get(frame) ?? 0) + 1);
          }
        }
      }
    }
  }
  return counts;
}

/**
 * Lists the entries that hold blocking cells at the authoring cell, the most placed first and by frame id after, each
 * with its misfit against the art. An entry at 4 px, `none`, or with no cells is not listed. A frame whose art gives no
 * footprint, or whose size is not known, is listed with no misfit.
 *
 * @param input The library, the derived footprints, the frame sizes and the placement counts.
 * @returns The rows.
 */
export function refitReport(input: RefitInput): RefitRow[] {
  const rows: RefitRow[] = [];
  for (const [id, entry] of Object.entries(input.overrides)) {
    if (entry === "none" || entry.cells === undefined || entry.cells.length === 0 || (entry.cell ?? FOOTPRINT_DEFAULT_CELL) !== FOOTPRINT_DEFAULT_CELL) {
      continue;
    }
    const base = input.derived.get(id);
    const size = input.size(id);
    const misfit = base === undefined || base === null || size === undefined ? null : footprintMisfit(base, footprintFromCells(entry.cells, size.w, size.h, FOOTPRINT_DEFAULT_CELL));
    rows.push({ frameId: id, placed: input.placed.get(id) ?? 0, cells: entry.cells.length, misfit });
  }
  return rows.sort((a, b) => b.placed - a.placed || (a.frameId < b.frameId ? -1 : a.frameId > b.frameId ? 1 : 0));
}
